/**
 * خدمات المهام: القراءة والإنشاء والتعديل والإسناد والإكمال والاعتماد.
 * تغيير الحالة يتم فقط عبر أوامر البوابة (وليست تحديثات مباشرة).
 */

import type {
  DataGateway,
  CreateTaskCommandInput,
  UpdateTaskCommandInput,
  PurgeCompletedTasksResult,
  Filter,
} from '@/data/types';
import type {
  AppNotification,
  AssignmentMode,
  Attachment,
  Task,
  TaskAssignee,
  TaskPriority,
  TaskStatus,
  TaskStatusHistory,
  TaskStep,
  TaskType,
  TaskVisibility,
  TaskCategory,
} from '@/domain/types';
import { newId } from '@/domain/ids';
import { AppError } from '@/domain/errors';
import { AUDIT_ACTIONS } from '@/domain/audit';
import { adaptTitleForBranch } from './template-service';

export type TaskView = 'board' | 'assigned' | 'created' | 'personal' | 'supervised' | 'overdue';

export interface TaskListFilters {
  status?: TaskStatus[];
  priority?: TaskPriority[];
  branchId?: string | null;
  brandId?: string | null;
  categoryId?: string | null;
  assigneeMembershipId?: string | null;
  from?: string | null;
  to?: string | null;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface TaskListResult {
  rows: Task[];
  total: number | null;
  page: number;
  pageSize: number;
}

export interface TaskDetails {
  task: Task;
  assignees: TaskAssignee[];
  steps: TaskStep[];
  history: TaskStatusHistory[];
  attachments: Attachment[];
  creatorName: string | null;
  category: TaskCategory | null;
}

export interface TaskActor {
  userId: string;
  membershipId: string | null;
}

const ACTIVE_STATUSES: TaskStatus[] = ['scheduled', 'pending', 'in_progress'];

async function assignedTaskIds(
  gateway: DataGateway,
  membershipIds: string[],
): Promise<string[]> {
  if (membershipIds.length === 0) return [];
  const rows = await gateway.table('task_assignees').list({
    filter: {
      membershipId: { op: 'in', value: membershipIds },
      unassignedAt: { op: 'isNull' },
    },
  });
  return [...new Set(rows.map((row) => row.taskId))];
}

export async function listTasks(
  gateway: DataGateway,
  input: {
    companyId: string;
    view: TaskView;
    actorMembershipId: string | null;
    isPlatformSupport?: boolean;
    supervisedMembershipIds: string[];
    filters?: TaskListFilters;
  },
): Promise<TaskListResult> {
  const filters = input.filters ?? {};
  const filter: Record<string, unknown> = { companyId: { op: 'eq', value: input.companyId } };

  if (filters.status && filters.status.length > 0) {
    filter['status'] = { op: 'in', value: filters.status };
  }
  if (filters.priority && filters.priority.length > 0) {
    filter['priority'] = { op: 'in', value: filters.priority };
  }
  if (filters.branchId) filter['branchId'] = { op: 'eq', value: filters.branchId };
  if (filters.brandId) filter['brandId'] = { op: 'eq', value: filters.brandId };
  if (filters.categoryId) filter['categoryId'] = { op: 'eq', value: filters.categoryId };
  if (filters.from) filter['localDate'] = { op: 'gte', value: filters.from };
  if (filters.to) filter['localDate'] = { op: 'lte', value: filters.to };

  const now = new Date().toISOString();
  switch (input.isPlatformSupport ? 'board' : input.view) {
    case 'assigned': {
      if (!input.actorMembershipId) { filter['id'] = { op: 'in', value: ['00000000-0000-0000-0000-000000000000'] }; break; }
      const ids = await assignedTaskIds(gateway, [input.actorMembershipId]);
      filter['id'] = { op: 'in', value: ids.length > 0 ? ids : ['00000000-0000-0000-0000-000000000000'] };
      break;
    }
    case 'personal':
      if (!input.actorMembershipId) { filter['id'] = { op: 'in', value: ['00000000-0000-0000-0000-000000000000'] }; break; }
      filter['isPersonal'] = { op: 'eq', value: true };
      filter['createdByMembershipId'] = { op: 'eq', value: input.actorMembershipId };
      break;
    case 'created':
      if (!input.actorMembershipId) { filter['id'] = { op: 'in', value: ['00000000-0000-0000-0000-000000000000'] }; break; }
      filter['createdByMembershipId'] = { op: 'eq', value: input.actorMembershipId };
      break;
    case 'supervised': {
      const ids = await assignedTaskIds(gateway, input.supervisedMembershipIds);
      filter['id'] = { op: 'in', value: ids.length > 0 ? ids : ['00000000-0000-0000-0000-000000000000'] };
      break;
    }
    case 'overdue':
      filter['dueAt'] = { op: 'lt', value: now };
      filter['status'] = { op: 'in', value: ACTIVE_STATUSES };
      break;
    case 'board':
    default:
      break;
  }

  if (filters.assigneeMembershipId) {
    const ids = await assignedTaskIds(gateway, [filters.assigneeMembershipId]);
    filter['id'] = { op: 'in', value: ids.length > 0 ? ids : ['00000000-0000-0000-0000-000000000000'] };
  }

  const page = Math.max(1, filters.page ?? 1);
  const pageSize = Math.min(200, Math.max(1, filters.pageSize ?? 50));
  const result = await gateway.table('tasks').query({
    filter: filter as Filter<Task>,
    orderBy: [
      { field: 'dueAt', direction: 'asc' },
      { field: 'createdAt', direction: 'desc' },
    ],
    offset: (page - 1) * pageSize,
    limit: pageSize,
    withCount: true,
  });

  let rows = result.rows;
  if (filters.search?.trim()) {
    const term = filters.search.trim().toLowerCase();
    rows = rows.filter(
      (task) =>
        task.title.toLowerCase().includes(term) ||
        (task.description ?? '').toLowerCase().includes(term),
    );
  }
  return { rows, total: result.total, page, pageSize };
}

export async function getTaskDetails(gateway: DataGateway, taskId: string): Promise<TaskDetails> {
  const task = await gateway.table('tasks').get(taskId);
  if (!task) throw AppError.notFound('المهمة غير موجودة');
  const [assignees, steps, history, attachments, creator, category] = await Promise.all([
    gateway.table('task_assignees').list({ filter: { taskId: { op: 'eq', value: taskId } } }),
    gateway.table('task_steps').list({
      filter: { taskId: { op: 'eq', value: taskId } },
      orderBy: [{ field: 'sortOrder', direction: 'asc' }],
    }),
    gateway.table('task_status_history').list({
      filter: { taskId: { op: 'eq', value: taskId } },
      orderBy: [{ field: 'createdAt', direction: 'asc' }],
    }),
    gateway.table('attachments').list({
      filter: { taskId: { op: 'eq', value: taskId }, deletedAt: { op: 'isNull' } },
      orderBy: [{ field: 'createdAt', direction: 'desc' }],
    }),
    task.createdByMembershipId
      ? gateway.table('memberships').get(task.createdByMembershipId)
          .then((membership) => (membership ? gateway.table('profiles').get(membership.userId) : null))
          .catch(() => null)
      : task.createdByUserId
        ? gateway.table('profiles').get(task.createdByUserId).catch(() => null)
        : Promise.resolve(null),
    gateway.table('task_categories').get(task.categoryId).catch(() => null),
  ]);
  return {
    task,
    assignees: assignees.filter((row) => !row.unassignedAt),
    steps,
    history,
    attachments: attachments.filter(
      (row) => row.uploadState !== 'deleted' && !row.storagePath.startsWith('tf-meta://'),
    ),
    creatorName: creator?.fullName ?? null,
    category,
  };
}

export async function listNotifications(
  gateway: DataGateway,
  companyId: string,
  limit = 50,
): Promise<AppNotification[]> {
  return gateway.table('notifications').list({
    filter: { companyId: { op: 'eq', value: companyId } },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
    limit,
  });
}

export async function createTask(
  gateway: DataGateway,
  actor: TaskActor,
  input: Omit<CreateTaskCommandInput, 'actorMembershipId' | 'actorUserId'>,
): Promise<Task> {
  return gateway.commands.createTask({ ...input, actorMembershipId: actor.membershipId, actorUserId: actor.userId });
}

export async function updateTask(
  gateway: DataGateway,
  actor: TaskActor,
  input: Omit<UpdateTaskCommandInput, 'actorMembershipId' | 'actorUserId'>,
): Promise<Task> {
  return gateway.commands.updateTask({ ...input, actorMembershipId: actor.membershipId, actorUserId: actor.userId });
}

export async function assignTask(
  gateway: DataGateway,
  actor: TaskActor,
  taskId: string,
  membershipIds: string[],
): Promise<Task> {
  return gateway.commands.assignTask({
    taskId,
    membershipIds,
    actorMembershipId: actor.membershipId,
    actorUserId: actor.userId,
  });
}

export async function completeTask(
  gateway: DataGateway,
  actor: TaskActor,
  input: { taskId: string; proofNote: string | null; attachmentIds: string[] },
): Promise<Task> {
  return gateway.commands.completeTask({
    taskId: input.taskId,
    membershipId: actor.membershipId,
    proofNote: input.proofNote,
    attachmentIds: input.attachmentIds,
    actorUserId: actor.userId,
  });
}

export async function decideApproval(
  gateway: DataGateway,
  actor: TaskActor,
  input: { taskId: string; decision: 'approve' | 'return'; reason: string | null },
): Promise<Task> {
  return gateway.commands.decideApproval({
    taskId: input.taskId,
    membershipId: actor.membershipId,
    decision: input.decision,
    reason: input.reason,
    actorUserId: actor.userId,
  });
}

export async function reopenTask(
  gateway: DataGateway,
  actor: TaskActor,
  taskId: string,
  reason: string | null,
): Promise<Task> {
  return gateway.commands.reopenTask({
    taskId,
    membershipId: actor.membershipId,
    actorUserId: actor.userId,
    reason,
  });
}

export async function cancelTask(
  gateway: DataGateway,
  actor: TaskActor,
  taskId: string,
  reason: string | null,
): Promise<Task> {
  return gateway.commands.cancelTask({
    taskId,
    membershipId: actor.membershipId,
    actorUserId: actor.userId,
    reason,
  });
}

export async function deleteTask(
  gateway: DataGateway,
  actor: TaskActor,
  taskId: string,
): Promise<void> {
  return gateway.commands.deleteTask({
    taskId,
    actorUserId: actor.userId,
  });
}

export async function deleteTasksBatch(
  gateway: DataGateway,
  actor: TaskActor,
  taskIds: string[],
): Promise<number> {
  let deleted = 0;
  for (const id of taskIds) {
    await gateway.commands.deleteTask({
      taskId: id,
      actorUserId: actor.userId,
    });
    deleted += 1;
  }
  return deleted;
}

export interface CloneTaskToBranchesInput {
  sourceTaskId: string;
  targetBranchIds: Array<string | null>;
  customBaseTitle?: string;
  autoAdjustBranchName: boolean;
  assigneeMode: 'branch_scope' | 'same_assignees' | 'custom';
  customAssigneeIds: string[];
  startAt?: string;
  dueAt?: string | null;
  branchNames: Map<string, string>;
  branchBrandMap: Map<string, string | null>;
}

export async function cloneTaskToBranches(
  gateway: DataGateway,
  actor: TaskActor & { companyId: string },
  input: CloneTaskToBranchesInput,
): Promise<Task[]> {
  const details = await getTaskDetails(gateway, input.sourceTaskId);
  const source = details.task;
  if (source.companyId !== actor.companyId) {
    throw AppError.forbidden('المهمة تابعة لمنشأة أخرى');
  }
  if (input.targetBranchIds.length === 0) {
    throw AppError.validation('اختر فرعًا واحدًا على الأقل للاستنساخ');
  }

  const baseTitle = input.customBaseTitle?.trim() || source.title;
  const stepTitles = details.steps.map((s) => s.title);
  const sourceAssigneeIds = details.assignees.map((a) => a.membershipId);
  const startAt = input.startAt || source.startAt;
  const dueAt = input.dueAt !== undefined ? input.dueAt : source.dueAt;
  const localDate = startAt.slice(0, 10) || source.localDate;

  const createdTasks: Task[] = [];
  for (const targetBranchId of input.targetBranchIds) {
    const nextTitle = adaptTitleForBranch({
      baseTitle,
      sourceBranchId: source.branchId,
      targetBranchId,
      branchNames: input.branchNames,
      autoAdjustBranchName: input.autoAdjustBranchName,
    });

    const targetBrandId = targetBranchId
      ? (input.branchBrandMap.get(targetBranchId) ?? source.brandId)
      : source.brandId;

    const assignmentMode: AssignmentMode =
      input.assigneeMode === 'branch_scope'
        ? 'scope'
        : input.assigneeMode === 'custom'
          ? input.customAssigneeIds.length > 1
            ? 'multi'
            : 'single'
          : source.assignmentMode;

    const assigneeMembershipIds =
      input.assigneeMode === 'branch_scope'
        ? []
        : input.assigneeMode === 'custom'
          ? input.customAssigneeIds
          : sourceAssigneeIds.length > 0
            ? sourceAssigneeIds
            : source.assigneeScope.membershipIds;

    const cloned = await createTask(gateway, actor, {
      task: {
        brandId: targetBrandId,
        branchId: targetBranchId,
        categoryId: source.categoryId,
        title: nextTitle,
        description: source.description,
        instructions: source.instructions,
        taskType: source.taskType,
        priority: source.priority,
        isPersonal: source.isPersonal,
        visibility: source.visibility,
        assignmentMode,
        completionRule: source.completionRule,
        requiresProof: source.requiresProof,
        requiresApproval: source.requiresApproval,
        approverMembershipId: source.approverMembershipId,
        assigneeMembershipIds,
        startAt,
        dueAt,
        localDate,
        timeZone: source.timeZone,
      },
      steps: stepTitles,
    });
    createdTasks.push(cloned);
  }

  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.taskCloned,
    entityType: 'task',
    entityId: source.id,
    after: {
      sourceTitle: source.title,
      targetBranchIds: input.targetBranchIds,
      clonedTaskIds: createdTasks.map((t) => t.id),
    },
  });

  return createdTasks;
}

export async function purgeCompletedTasks(
  gateway: DataGateway,
  actor: { companyId: string; userId: string },
  input: { olderThanDays: number; dryRun?: boolean },
): Promise<PurgeCompletedTasksResult> {
  return gateway.commands.purgeCompletedTasks({
    companyId: actor.companyId,
    actorUserId: actor.userId,
    olderThanDays: input.olderThanDays,
    dryRun: input.dryRun ?? false,
  });
}

export async function addStep(
  gateway: DataGateway,
  taskId: string,
  title: string,
  existingCount: number,
): Promise<TaskStep> {
  const clean = title.trim();
  if (clean.length < 2) throw AppError.validation('عنوان الخطوة قصير جدًا');
  return gateway.table('task_steps').insert({
    id: newId(),
    taskId,
    title: clean,
    sortOrder: existingCount,
    isDone: false,
    doneByMembershipId: null,
    doneAt: null,
  });
}

export async function toggleStep(
  gateway: DataGateway,
  actor: TaskActor,
  step: TaskStep,
): Promise<TaskStep> {
  return gateway.table('task_steps').update(step.id, {
    isDone: !step.isDone,
    doneByMembershipId: !step.isDone ? actor.membershipId : null,
    doneAt: !step.isDone ? new Date().toISOString() : null,
  });
}

export function isTaskOverdue(task: Task, now = Date.now()): boolean {
  if (task.status === 'completed' || task.status === 'cancelled') return false;
  return task.dueAt !== null && new Date(task.dueAt).getTime() < now;
}

export function taskStatusLabel(status: TaskStatus): string {
  const labels: Record<TaskStatus, string> = {
    scheduled: 'مجدولة',
    pending: 'قيد الانتظار',
    in_progress: 'جارية',
    completed: 'مكتملة',
    cancelled: 'ملغاة',
  };
  return labels[status];
}

export function priorityLabel(priority: TaskPriority): string {
  const labels: Record<TaskPriority, string> = {
    low: 'منخفضة',
    normal: 'عادية',
    high: 'عالية',
    urgent: 'عاجلة',
  };
  return labels[priority];
}

export function taskTypeLabel(type: TaskType): string {
  const labels: Record<TaskType, string> = {
    personal: 'شخصية',
    operational: 'تشغيلية',
    administrative: 'إدارية',
  };
  return labels[type];
}

export function visibilityLabel(visibility: TaskVisibility): string {
  const labels: Record<TaskVisibility, string> = {
    private: 'خاصة',
    assignees: 'للمسند إليهم',
    company: 'للمنشأة',
  };
  return labels[visibility];
}

export async function taskCounts(
  gateway: DataGateway,
  input: {
    companyId: string;
    membershipId: string | null;
    supervisedMembershipIds: string[];
    isPlatformSupport?: boolean;
    userId?: string;
    activeTasks?: Task[];
    assignedTasks?: TaskListResult;
    completedCount?: number;
  },
): Promise<{ assigned: number; created: number; overdue: number; pendingApproval: number; personal: number; completed: number }> {
  if (input.isPlatformSupport) {
    const overdueCount = input.activeTasks
      ? input.activeTasks.filter((task) => isTaskOverdue(task)).length
      : null;
    const [created, overdueTasks, completed, pendingApprovalResult] = await Promise.all([
      input.userId
        ? gateway.table('tasks').query({
            filter: { companyId: { op: 'eq', value: input.companyId }, createdByUserId: { op: 'eq', value: input.userId }, deletedAt: { op: 'isNull' } },
            limit: 1,
            withCount: true,
          })
        : Promise.resolve({ total: 0 }),
      overdueCount === null
        ? listTasks(gateway, {
            companyId: input.companyId,
            actorMembershipId: null,
            isPlatformSupport: true,
            supervisedMembershipIds: [],
            view: 'overdue',
            filters: { pageSize: 1 },
          })
        : Promise.resolve({ total: overdueCount, rows: [] }),
      listTasks(gateway, {
        companyId: input.companyId,
        actorMembershipId: null,
        isPlatformSupport: true,
        supervisedMembershipIds: [],
        view: 'board',
        filters: { status: ['completed'], pageSize: 1 },
      }),
      gateway.table('tasks').query({
        filter: {
          companyId: { op: 'eq', value: input.companyId },
          approvalState: { op: 'eq', value: 'pending' },
          deletedAt: { op: 'isNull' },
        },
        limit: 1,
        withCount: true,
      }),
    ]);
    return {
      assigned: 0,
      created: created.total ?? 0,
      overdue: overdueCount ?? overdueTasks.total ?? overdueTasks.rows.length,
      pendingApproval: pendingApprovalResult.total ?? 0,
      personal: 0,
      completed: completed.total ?? completed.rows.length,
    };
  }
  const base = {
    companyId: input.companyId,
    actorMembershipId: input.membershipId,
    supervisedMembershipIds: input.supervisedMembershipIds,
  };

  if (input.activeTasks) {
    const overdue = input.activeTasks.filter((task) => isTaskOverdue(task)).length;
    const pendingApproval = input.activeTasks.filter((task) => task.approvalState === 'pending').length;
    const created = input.activeTasks.filter(
      (task) =>
        (input.membershipId && task.createdByMembershipId === input.membershipId) ||
        (input.userId && task.createdByUserId === input.userId),
    ).length;
    const personal = input.activeTasks.filter(
      (task) =>
        task.isPersonal &&
        ((input.membershipId && task.createdByMembershipId === input.membershipId) ||
          (input.userId && task.createdByUserId === input.userId)),
    ).length;

    const assigned =
      input.assignedTasks ??
      (await listTasks(gateway, { ...base, view: 'assigned', filters: { pageSize: 1 } }));
    const completedCount =
      input.completedCount !== undefined
        ? input.completedCount
        : (await listTasks(gateway, { ...base, view: 'board', filters: { status: ['completed'], pageSize: 1 } })).total ?? 0;

    return {
      assigned: assigned.total ?? assigned.rows.length,
      created,
      overdue,
      pendingApproval,
      personal,
      completed: completedCount,
    };
  }

  const [assigned, created, personal, overdueTasks, completed, pendingApprovalResult] = await Promise.all([
    listTasks(gateway, { ...base, view: 'assigned', filters: { pageSize: 1 } }),
    listTasks(gateway, { ...base, view: 'created', filters: { pageSize: 1 } }),
    listTasks(gateway, { ...base, view: 'personal', filters: { pageSize: 1 } }),
    listTasks(gateway, { ...base, view: 'overdue', filters: { pageSize: 1 } }),
    listTasks(gateway, { ...base, view: 'board', filters: { status: ['completed'], pageSize: 1 } }),
    gateway.table('tasks').query({
      filter: {
        companyId: { op: 'eq', value: input.companyId },
        approvalState: { op: 'eq', value: 'pending' },
        deletedAt: { op: 'isNull' },
      },
      limit: 1,
      withCount: true,
    }),
  ]);
  return {
    assigned: assigned.total ?? assigned.rows.length,
    created: created.total ?? created.rows.length,
    overdue: overdueTasks.total ?? overdueTasks.rows.length,
    pendingApproval: pendingApprovalResult.total ?? 0,
    personal: personal.total ?? personal.rows.length,
    completed: completed.total ?? completed.rows.length,
  };
}
